import { Box, Grid, IconButton, Typography, Container, alpha, Chip, Button, Paper } from "@mui/material";
import { StatCard } from "./StatCard";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import {
    Home as HomeIcon,
    ArrowBack as ArrowBackIcon,
    Apartment as ApartmentIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Add,
} from '@mui/icons-material';
import { getStatusColor, getStatusText } from "../helpers";
import CustomAppBar from "../../../components/shared/CustomAppBar";
import { useNavigate, useParams } from "react-router/dist";
import { usePropertyInventory } from "../../../hooks/usePropertyInventory";
import { useEffect } from "react";
import { ApartmentDto } from "../../../types/propertyInventory/Apartment";

export const ApartmentMapView = () => {
    const { id } = useParams();
    const { getApartmentsByComplexId, apartmentsByComplex, getApartmentComplexById, complex } = usePropertyInventory();
    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            if (id) {
                await getApartmentComplexById(id);
            }
        })();
    }, [id, getApartmentComplexById]);

    useEffect(() => {
        (async () => {
            if (id) {
                await getApartmentsByComplexId(id);
            }
        })();
    }, [id, getApartmentsByComplexId]);

    const availableUnits = apartmentsByComplex.filter((apt: any) => apt.status === 1).length;
    const occupiedUnits = apartmentsByComplex.filter((apt: any) => apt.status === 2).length;
    const maintenanceUnits = apartmentsByComplex.filter((apt: any) => apt.status === 3).length;

    const pieData = [
        { name: 'Disponible', value: availableUnits, color: '#4CAF50' },
        { name: 'Ocupado', value: occupiedUnits, color: '#FF9800' },
        { name: 'No disponible', value: maintenanceUnits, color: '#F44336' },
    ];

    return (
        <>
            <CustomAppBar />
            <Box sx={{ backgroundColor: 'white', minHeight: '100vh', mt: '10vh' }}>
                {/* Header personalizado */}
                <Box sx={{
                    p: 3,
                    borderBottom: '1px solid #E0E0E0',
                    backgroundColor: 'white',
                    mb: 3
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton
                            onClick={() => navigate(-1)}
                            sx={{
                                mr: 2,
                                backgroundColor: alpha('#4DB6AC', 0.1),
                                '&:hover': {
                                    backgroundColor: alpha('#4DB6AC', 0.2),
                                }
                            }}
                        >
                            <ArrowBackIcon sx={{ color: '#4DB6AC' }} />
                        </IconButton>
                        <ApartmentIcon sx={{ mr: 2, color: '#4DB6AC' }} />
                        <Typography variant="h5" component="div" fontWeight="bold" color="#2E7D7A">
                            {complex?.name} - Apartamentos
                        </Typography>
                    </Box>
                </Box>

                <Container maxWidth="lg">
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h5" gutterBottom color="#2E7D7A" fontWeight="bold">
                            Distribución de Apartamentos
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                            Vista interactiva de las unidades disponibles en {complex?.name}
                        </Typography>

                        {/* Cards de estadísticas */}
                        <Grid container spacing={3} sx={{ mb: 4 }}>
                            <Grid item xs={12} sm={4}>
                                <StatCard
                                    title="Unidades Disponibles"
                                    value={availableUnits}
                                    color="#4CAF50"
                                    icon={<CheckCircleIcon sx={{ fontSize: 30, color: '#4CAF50' }} />}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <StatCard
                                    title="Unidades Ocupadas"
                                    value={occupiedUnits}
                                    color="#FF9800"
                                    icon={<CancelIcon sx={{ fontSize: 30, color: '#FF9800' }} />}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <StatCard
                                    title="Total de Unidades"
                                    value={apartmentsByComplex.length}
                                    color="#2196F3"
                                    icon={<HomeIcon sx={{ fontSize: 30, color: '#2196F3' }} />}
                                />
                            </Grid>
                        </Grid>

                        {/* Gráfico de pastel */}
                        {apartmentsByComplex.length > 0 && (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            mb: 4,
                            p: 3,
                            backgroundColor: 'white',
                            borderRadius: 3,
                            boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                        }}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h6" color="#2E7D7A" fontWeight="bold" sx={{ mb: 2 }}>
                                    Distribución de Unidades
                                </Typography>
                                <Box sx={{ height: 250, width: 300 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={pieData}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={100}
                                                paddingAngle={3}
                                                dataKey="value"
                                            >
                                                {pieData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                            <Legend />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </Box>
                            </Box>
                        </Box>
                        )}

                        {(apartmentsByComplex.length > 0) && (
                        <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
                            <Chip
                                label="Disponible"
                                sx={{ backgroundColor: alpha('#4CAF50', 0.1), color: '#388E3C' }}
                            />
                            <Chip
                                label="Ocupado"
                                sx={{ backgroundColor: alpha('#FF9800', 0.1), color: '#F57C00' }}
                            />
                            <Chip
                                label="No disponible"
                                sx={{ backgroundColor: alpha('#F44336', 0.1), color: '#D32F2F' }}
                            />
                        </Box>
                        )}

                        {/* Mapa de apartamentos */}
                        <Paper>
                            {apartmentsByComplex.length > 0 && (
                            <Button
                                variant="contained"
                                startIcon={<Add />}
                                sx={{ mb: 2, borderRadius: 3, ml: 2, backgroundColor: '#4DB6AC', '&:hover': { backgroundColor: '#26A69A' } }}
                                onClick={() => navigate(`/properties-inventory/complex/${complex?.id}/apartment/create-apartment`)}
                            >
                                Agregar Apartamentos
                            </Button>
                            )}
                            {apartmentsByComplex.length === 0 && (
                                <Box sx={{
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    py: 6,
                                    px: 3,
                                    textAlign: 'center',
                                    border: '2px dashed rgba(0,0,0,0.06)',
                                    borderRadius: 2,
                                    background: 'linear-gradient(180deg, rgba(76,175,80,0.03), transparent)'
                                }}>
                                    <ApartmentIcon sx={{ fontSize: 48, color: '#4DB6AC', mb: 1 }} />
                                    <Typography variant="h6" fontWeight="bold" color="#2E7D7A">
                                        Sin apartamentos por ahora
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2, maxWidth: 420 }}>
                                        No hay apartamentos disponibles para mostrar. Agrega el primero para comenzar a poblar este complejo.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        startIcon={<Add />}
                                        onClick={() => navigate(`/properties-inventory/complex/${complex?.id}/apartment/create-apartment`)}
                                        sx={{ backgroundColor: '#4DB6AC', '&:hover': { backgroundColor: '#26A69A' }, borderRadius: 2 }}
                                    >
                                        Agregar apartamentos
                                    </Button>
                                </Box>
                            )}
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 2,
                                justifyContent: 'center',
                                p: 4,
                                backgroundColor: '#FAFAFA',
                                borderRadius: 3,
                                border: '1px solid #E0E0E0',
                            }}
                        >
                            {apartmentsByComplex.map((apartment: ApartmentDto) => (
                                <Box
                                    key={apartment.id}
                                    onClick={() => navigate(`/properties-inventory/complex/${complex?.id}/apartment/${apartment.id}`)}
                                    sx={{
                                        width: 120,
                                        height: 120,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: 3,
                                        border: '2px solid',
                                        borderColor: getStatusColor(apartment.status),
                                        backgroundColor: alpha(getStatusColor(apartment.status), 0.1),
                                        cursor: 'pointer',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&:hover': {
                                            transform: 'translateY(-5px) scale(1.05)',
                                            boxShadow: `0 10px 30px ${alpha(getStatusColor(apartment.status), 0.3)}`,
                                            borderColor: getStatusColor(apartment.status),
                                        }
                                    }}
                                >
                                    <HomeIcon
                                        sx={{
                                            fontSize: 40,
                                            color: getStatusColor(apartment.status),
                                            mb: 1
                                        }}
                                    />
                                    <Typography
                                        variant="body2"
                                        fontWeight="bold"
                                        color={getStatusColor(apartment.status)}
                                        sx={{ textAlign: 'center' }}
                                    >
                                        {apartment.apartmentNumber}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontSize: '0.7rem',
                                            color: 'text.secondary',
                                            textAlign: 'center'
                                        }}
                                    >
                                        {getStatusText(apartment.status)}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                        </Paper>
                    </Box>
                </Container>
            </Box>
        </>

    );
};